import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCopy } from "react-icons/fa";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import useReaderStore from "../../context/readerStore";
import { showInfoNotification } from "../../services/notificationService";
import "./PublicationDetails.css";

/**
 * Display fields taken only from metadata.json bibliographic_data
 * (plus top-level content_type). Keys match the JSON property names.
 */
const BIBLIO_FIELD_KEYS = [
  "title",
  "author",
  "code",
  "type",
  "language",
  "original_publisher",
  "series",
  "edition",
  "pages",
  "pisbn13",
  "publication_year",
  "copyright_year",
  "read_direction",
];

const isBlank = (value) => {
  if (value == null) return true;
  if (typeof value === "boolean") return false;
  return String(value).trim() === "";
};

const asDisplayString = (value) => {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value).trim();
};

const splitDescriptionParagraphs = (description) => {
  const text = String(description || "").trim();
  if (!text) return [];
  return text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.replace(/\s+/g, " ").trim())
    .filter(Boolean);
};

const getBibliographicData = (metadata) => {
  if (!metadata) return null;
  return (
    metadata.metadata?.bibliographic_data ||
    metadata.rich_metadata?.metadata?.bibliographic_data ||
    metadata.bibliographic_data ||
    null
  );
};

/**
 * Build sidebar values strictly from metadata.json fields — no invented keys.
 */
export const buildPublicationFromMetadata = (metadata) => {
  const biblio = getBibliographicData(metadata);
  if (!biblio && !metadata) return null;

  const fields = {};
  BIBLIO_FIELD_KEYS.forEach((key) => {
    fields[key] = asDisplayString(biblio?.[key]);
  });

  // Top-level metadata.json field (not inside bibliographic_data).
  fields.content_type = asDisplayString(metadata?.content_type);

  // Fallback title from top-level metadata.json when biblio.title is empty.
  if (isBlank(fields.title)) {
    fields.title = asDisplayString(metadata?.title);
  }

  // Fallback pages from top-level total_pages when biblio.pages is empty.
  if (isBlank(fields.pages)) {
    fields.pages = asDisplayString(metadata?.total_pages);
  }

  return {
    fields,
    descriptionParagraphs: splitDescriptionParagraphs(biblio?.description),
    hasBibliographicData: Boolean(biblio),
  };
};

const FIELD_ORDER = [...BIBLIO_FIELD_KEYS, "content_type"];

const hasAnyPublicationData = (publication) => {
  if (!publication) return false;
  return (
    FIELD_ORDER.some((key) => !isBlank(publication.fields?.[key])) ||
    publication.descriptionParagraphs.length > 0
  );
};

const formatPublicationForClipboard = (publication, t) => {
  const emptyLabel = t("publicationDetails.noData");
  const lines = FIELD_ORDER.map((key) => {
    const label = t(`publicationDetails.fields.${key}`);
    const value = publication.fields?.[key];
    return `${label}: ${isBlank(value) ? emptyLabel : value}`;
  });

  const descriptionHeading = t("publicationDetails.description");
  const descriptionBody =
    publication.descriptionParagraphs.join("\n\n") || emptyLabel;
  return [...lines, "", `${descriptionHeading}:`, descriptionBody].join("\n");
};

const FieldValue = ({ value, emptyLabel }) => {
  if (isBlank(value)) {
    return <span className="publication-details-empty">{emptyLabel}</span>;
  }
  return value;
};

const PublicationDetails = () => {
  const { t } = useTranslation();
  const {
    isPublicationDetailsSidebarOpen,
    togglePublicationDetailsSidebar,
    metadata,
  } = useReaderStore();
  const [copyStatus, setCopyStatus] = useState("idle");
  const copyResetTimeoutRef = useRef(null);

  const publication = useMemo(
    () => buildPublicationFromMetadata(metadata),
    [metadata],
  );
  const hasData = hasAnyPublicationData(publication);
  const emptyLabel = t("publicationDetails.noData");

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    if (!publication || !hasData) return;
    const text = formatPublicationForClipboard(publication, t);
    try {
      await navigator.clipboard.writeText(text);
      showInfoNotification("publicationDetails.copied");
      setCopyStatus("copied");
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = window.setTimeout(() => {
        setCopyStatus("idle");
      }, 2000);
    } catch (err) {
      console.error("Could not copy publication details:", err);
      setCopyStatus("idle");
    }
  }, [publication, hasData, t]);

  const copyLabel =
    copyStatus === "copied"
      ? t("publicationDetails.copied")
      : t("publicationDetails.copy");

  return (
    <div
      className={`publication-details ${
        isPublicationDetailsSidebarOpen ? "open" : ""
      }`}
      role="complementary"
      aria-label={t("publicationDetails.title")}
    >
      <div className="publication-details-header">
        <div className="publication-details-header-left">
          <button
            type="button"
            className="publication-details-icon-button"
            onClick={togglePublicationDetailsSidebar}
            aria-label={t("publicationDetails.close")}
            title={t("publicationDetails.close")}
          >
            <CloseIcon width={20} height={20} aria-hidden="true" />
          </button>
          <h3 tabIndex="-1">{t("publicationDetails.title")}</h3>
        </div>

        <div className="publication-details-header-right">
          <button
            type="button"
            className="publication-details-icon-button"
            onClick={handleCopy}
            disabled={!hasData}
            aria-label={copyLabel}
            title={copyLabel}
          >
            <FaCopy aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="publication-details-content">
        {!hasData ? (
          <p className="publication-details-empty-state" role="status">
            {t("publicationDetails.empty")}
          </p>
        ) : (
          <>
            <dl className="publication-details-fields">
              {FIELD_ORDER.map((key) => (
                <div key={key} className="publication-details-row">
                  <dt>{t(`publicationDetails.fields.${key}`)}</dt>
                  <dd>
                    <FieldValue
                      value={publication.fields[key]}
                      emptyLabel={emptyLabel}
                    />
                  </dd>
                </div>
              ))}
            </dl>

            <section
              className="publication-details-description"
              aria-labelledby="publication-details-description-heading"
            >
              <h4 id="publication-details-description-heading">
                {t("publicationDetails.description")}
              </h4>
              {publication.descriptionParagraphs.length === 0 ? (
                <p className="publication-details-empty">{emptyLabel}</p>
              ) : (
                publication.descriptionParagraphs.map((paragraph, index) => (
                  <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>
                ))
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicationDetails;
